import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft, CheckCircle, XCircle, Clock, CreditCard, Building2, RefreshCw, Eye,
  Mail, Calculator, QrCode, Copy, Check, Download, Plus, Minus, DollarSign, Users,
  Calendar, TrendingDown, Upload, Link2, Unlink, RotateCcw, Trash2, AlertTriangle,
  FileText, Send, Ban
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import html2canvas from "html2canvas";
import TeamAssignmentPanel from "../components/admin/TeamAssignmentPanel";
import CoachAssignmentPanel from "../components/admin/CoachAssignmentPanel";
import AttendanceOverviewPanel from "../components/admin/AttendanceOverviewPanel";
import { formatAdminError } from "../lib/adminError";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ===== SGQR PayNow QR Code Generation (EMVCo format) =====
const CRC_TABLE = [
  0x0000, 0x1021, 0x2042, 0x3063, 0x4084, 0x50a5, 0x60c6, 0x70e7,
  0x8108, 0x9129, 0xa14a, 0xb16b, 0xc18c, 0xd1ad, 0xe1ce, 0xf1ef,
  0x1231, 0x0210, 0x3273, 0x2252, 0x52b5, 0x4294, 0x72f7, 0x62d6,
  0x9339, 0x8318, 0xb37b, 0xa35a, 0xd3bd, 0xc39c, 0xf3ff, 0xe3de,
  0x2462, 0x3443, 0x0420, 0x1401, 0x64e6, 0x74c7, 0x44a4, 0x5485,
  0xa56a, 0xb54b, 0x8528, 0x9509, 0xe5ee, 0xf5cf, 0xc5ac, 0xd58d,
  0x3653, 0x2672, 0x1611, 0x0630, 0x76d7, 0x66f6, 0x5695, 0x46b4,
  0xb75b, 0xa77a, 0x9719, 0x8738, 0xf7df, 0xe7fe, 0xd79d, 0xc7bc,
  0x48c4, 0x58e5, 0x6886, 0x78a7, 0x0840, 0x1861, 0x2802, 0x3823,
  0xc9cc, 0xd9ed, 0xe98e, 0xf9af, 0x8948, 0x9969, 0xa90a, 0xb92b,
  0x5af5, 0x4ad4, 0x7ab7, 0x6a96, 0x1a71, 0x0a50, 0x3a33, 0x2a12,
  0xdbfd, 0xcbdc, 0xfbbf, 0xeb9e, 0x9b79, 0x8b58, 0xbb3b, 0xab1a,
  0x6ca6, 0x7c87, 0x4ce4, 0x5cc5, 0x2c22, 0x3c03, 0x0c60, 0x1c41,
  0xedae, 0xfd8f, 0xcdec, 0xddcd, 0xad2a, 0xbd0b, 0x8d68, 0x9d49,
  0x7e97, 0x6eb6, 0x5ed5, 0x4ef4, 0x3e13, 0x2e32, 0x1e51, 0x0e70,
  0xff9f, 0xefbe, 0xdfdd, 0xcffc, 0xbf1b, 0xaf3a, 0x9f59, 0x8f78,
  0x9188, 0x81a9, 0xb1ca, 0xa1eb, 0xd10c, 0xc12d, 0xf14e, 0xe16f,
  0x1080, 0x00a1, 0x30c2, 0x20e3, 0x5004, 0x4025, 0x7046, 0x6067,
  0x83b9, 0x9398, 0xa3fb, 0xb3da, 0xc33d, 0xd31c, 0xe37f, 0xf35e,
  0x02b1, 0x1290, 0x22f3, 0x32d2, 0x4235, 0x5214, 0x6277, 0x7256,
  0xb5ea, 0xa5cb, 0x95a8, 0x8589, 0xf56e, 0xe54f, 0xd52c, 0xc50d,
  0x34e2, 0x24c3, 0x14a0, 0x0481, 0x7466, 0x6447, 0x5424, 0x4405,
  0xa7db, 0xb7fa, 0x8799, 0x97b8, 0xe75f, 0xf77e, 0xc71d, 0xd73c,
  0x26d3, 0x36f2, 0x0691, 0x16b0, 0x6657, 0x7676, 0x4615, 0x5634,
  0xd94c, 0xc96d, 0xf90e, 0xe92f, 0x99c8, 0x89e9, 0xb98a, 0xa9ab,
  0x5844, 0x4865, 0x7806, 0x6827, 0x18c0, 0x08e1, 0x3882, 0x28a3,
  0xcb7d, 0xdb5c, 0xeb3f, 0xfb1e, 0x8bf9, 0x9bd8, 0xabbb, 0xbb9a,
  0x4a75, 0x5a54, 0x6a37, 0x7a16, 0x0af1, 0x1ad0, 0x2ab3, 0x3a92,
  0xfd2e, 0xed0f, 0xdd6c, 0xcd4d, 0xbdaa, 0xad8b, 0x9de8, 0x8dc9,
  0x7c26, 0x6c07, 0x5c64, 0x4c45, 0x3ca2, 0x2c83, 0x1ce0, 0x0cc1,
  0xef1f, 0xff3e, 0xcf5d, 0xdf7c, 0xaf9b, 0xbfba, 0x8fd9, 0x9ff8,
  0x6e17, 0x7e36, 0x4e55, 0x5e74, 0x2e93, 0x3eb2, 0x0ed1, 0x1ef0
];

function generatePayNowQR(amountVal) {
  const amountStr = parseFloat(amountVal).toFixed(2);
  const pad = (str, n) => str.toString().padStart(n, '0');

  const payload = [
    { id: '00', value: '01' },
    { id: '01', value: '12' },
    { id: '26', value: [
      { id: '00', value: 'SG.PAYNOW' },
      { id: '01', value: '3' },
      { id: '02', value: 'UEN201422384R#WISE' },
      { id: '03', value: '1' }
    ]},
    { id: '52', value: '0000' },
    { id: '53', value: '702' },
    { id: '54', value: amountStr },
    { id: '58', value: 'SG' },
    { id: '59', value: 'NA' },
    { id: '60', value: 'SINGAPORE' },
    { id: '62', value: [
      { id: '01', value: 'A21882030QR' }
    ]}
  ];

  let qrString = payload.reduce((result, current) => {
    let value = current.value;
    if (Array.isArray(value)) {
      value = value.reduce((nested, item) => {
        return nested + item.id + pad(item.value.length, 2) + item.value;
      }, "");
    }
    return result + current.id + pad(value.length, 2) + value;
  }, "");

  // CRC16 checksum
  let crc = 0xFFFF;
  const str = qrString + '6304';
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    const j = (c ^ (crc >> 8)) & 0xFF;
    crc = CRC_TABLE[j] ^ (crc << 8);
  }
  const crcHex = ((crc ^ 0) & 0xFFFF).toString(16).toUpperCase().padStart(4, '0');
  return qrString + '6304' + crcHex;
}

// ===== SUB-COMPONENTS =====

function SummaryCards({ pendingPayments, allPayments, disputedCount }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      <Card className="p-5 bg-[#0a0f1a] border-2 border-yellow-500/30" data-testid="pending-count-card">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-yellow-400 uppercase tracking-wide">Pending</p>
            <p className="text-3xl font-bold text-yellow-300 mt-1">{pendingPayments.length}</p>
          </div>
          <Clock className="w-9 h-9 text-yellow-500/60" />
        </div>
      </Card>
      <Card className="p-5 bg-[#0a0f1a] border-2 border-amber-500/30" data-testid="disputed-count-card">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-amber-400 uppercase tracking-wide">Disputed</p>
            <p className="text-3xl font-bold text-amber-300 mt-1">{disputedCount}</p>
          </div>
          <AlertTriangle className="w-9 h-9 text-amber-500/60" />
        </div>
      </Card>
      <Card className="p-5 bg-emerald-500/10 border-2 border-emerald-500/30" data-testid="paid-today-card">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-emerald-400 uppercase tracking-wide">Paid Today</p>
            <p className="text-3xl font-bold text-green-300 mt-1">
              {allPayments.filter(p =>
                (p.payment_status === "paid" || p.payment_status === "complete") &&
                p.paid_at && new Date(p.paid_at).toDateString() === new Date().toDateString()
              ).length}
            </p>
          </div>
          <CheckCircle className="w-9 h-9 text-emerald-400/60" />
        </div>
      </Card>
      <Card className="p-5 bg-[#0a0f1a] border-2 border-blue-500/30" data-testid="total-transactions-card">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-blue-400 uppercase tracking-wide">Total</p>
            <p className="text-3xl font-bold text-blue-300 mt-1">{allPayments.length}</p>
          </div>
          <CreditCard className="w-9 h-9 text-blue-400/60" />
        </div>
      </Card>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = {
    pending_verification: { color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", label: "Pending Verification" },
    awaiting_payment: { color: "bg-orange-500/20 text-orange-400 border-orange-500/30", label: "Awaiting Payment" },
    paid: { color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", label: "Paid" },
    complete: { color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", label: "Complete" },
    rejected: { color: "bg-red-500/20 text-red-400 border-red-500/30", label: "Rejected" },
    unpaid: { color: "bg-red-500/20 text-red-400 border-red-500/30", label: "Unpaid" },
    disputed: { color: "bg-amber-500/20 text-amber-400 border-amber-500/30", label: "Disputed" },
    pending: { color: "bg-slate-500/20 text-slate-300 border-slate-500/30", label: "Pending" },
    expired: { color: "bg-slate-500/20 text-slate-500 border-slate-600", label: "Expired" },
    initiated: { color: "bg-slate-500/20 text-slate-400 border-slate-600", label: "Initiated" },
  }[status] || { color: "bg-slate-800/50 text-white", label: status || "Unknown" };
  return <Badge variant="outline" className={`${config.color} font-medium`} data-testid={`status-badge-${status}`}>{config.label}</Badge>;
}

function MethodBadge({ method }) {
  if (method === "paynow") {
    return <Badge variant="outline" className="bg-purple-500/20 text-purple-400 border-purple-500/30 font-medium"><Building2 className="w-3 h-3 mr-1" />PayNow</Badge>;
  }
  return <Badge variant="outline" className="bg-blue-500/20 text-blue-400 border-blue-500/30 font-medium"><CreditCard className="w-3 h-3 mr-1" />Card</Badge>;
}

function ChildrenInfo({ payment }) {
  const childDetails = payment.fee_breakdown?.child_details || [];
  const childNames = payment.children_names || childDetails.map(c => c.name);
  if (!childNames?.length) return null;
  return (
    <div className="mt-1.5 p-1.5 bg-blue-500/10 rounded border border-blue-500/20">
      <p className="text-xs text-blue-400 font-medium">{childNames.join(", ")}</p>
      {childDetails.length > 0 && (
        <p className="text-xs text-slate-400 mt-0.5">
          {childDetails.reduce((acc, c) => acc + (c.session_count || 0), 0)} sessions/wk
        </p>
      )}
    </div>
  );
}

function RegistrationContext({ payment }) {
  const fb = payment.fee_breakdown || {};
  const dm = payment.include_digital_membership;
  const memberStatus = fb.member_status;
  const isNew = fb.registration_fee > 0;
  return (
    <div className="flex flex-wrap gap-1">
      {memberStatus?.is_member ? (
        <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">Member</span>
      ) : (
        <span className="text-xs px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">Non-Member</span>
      )}
      {dm && <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">+DM</span>}
      {isNew && <span className="text-xs px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-400">New Reg</span>}
      {fb.term_name && <span className="text-xs text-blue-400 block mt-0.5">{fb.term_name}</span>}
    </div>
  );
}

// ===== MAIN COMPONENT =====

export default function AdminPaymentVerification({ user, logout }) {
  const [pendingPayments, setPendingPayments] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Approve dialog state
  const [approveDialog, setApproveDialog] = useState({ open: false, payment: null });
  const [approveData, setApproveData] = useState({ bankRef: "", payeeName: "", approveChildren: true, notes: "" });

  // Reject dialog state
  const [rejectDialog, setRejectDialog] = useState({ open: false, payment: null });
  const [rejectionReason, setRejectionReason] = useState("");

  // Dispute dialog state
  const [disputeDialog, setDisputeDialog] = useState({ open: false, payment: null });
  const [disputeData, setDisputeData] = useState({ reason: "", receivedAmount: "" });

  // Revert dialog state
  const [revertDialog, setRevertDialog] = useState({ open: false, payment: null });
  const [revertReason, setRevertReason] = useState("");

  // Feb 2026 — Mark as Refunded
  const [refundDialog, setRefundDialog] = useState({ open: false, payment: null });
  const [refundReason, setRefundReason] = useState("");

  // Delete confirm state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, payment: null });

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [docFilter, setDocFilter] = useState("all");
  const [previewDoc, setPreviewDoc] = useState(null);
  const [generatingReceipts, setGeneratingReceipts] = useState(false);

  // Admin Create Payment state
  const [parentSearch, setParentSearch] = useState("");
  const [parentResults, setParentResults] = useState([]);
  const [selectedParent, setSelectedParent] = useState(null);
  const [createPaymentChildren, setCreatePaymentChildren] = useState([{ given_name: "", last_name: "", birthdate: "", gender: "male", medical_conditions: "" }]);
  const [createPaymentTerm, setCreatePaymentTerm] = useState("");
  const [createPaymentWeeks, setCreatePaymentWeeks] = useState(10);
  const [createPaymentSessions, setCreatePaymentSessions] = useState(1);
  const [createPaymentIsMember, setCreatePaymentIsMember] = useState(false);
  const [createPaymentRegFee, setCreatePaymentRegFee] = useState(false);
  const [createPaymentDM, setCreatePaymentDM] = useState(false);
  const [createPaymentNotes, setCreatePaymentNotes] = useState("");
  const [createPaymentNewParent, setCreatePaymentNewParent] = useState({ name: "", email: "", phone: "" });
  const [creatingPayment, setCreatingPayment] = useState(false);
  const [searchingParents, setSearchingParents] = useState(false);

  // Bank upload state
  const [bankUploads, setBankUploads] = useState([]);
  const [bankEntries, setBankEntries] = useState([]);
  const [bankFilter, setBankFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [linkDialog, setLinkDialog] = useState({ open: false, entry: null });
  const [linkPaymentId, setLinkPaymentId] = useState("");
  const fileInputRef = useRef(null);

  // Fee Calculator state
  const [showFeeCalculator, setShowFeeCalculator] = useState(false);
  const [showFeeResult, setShowFeeResult] = useState(false);
  const [feeCalcResult, setFeeCalcResult] = useState(null);
  const [calculatingFee, setCalculatingFee] = useState(false);
  const [terms, setTerms] = useState([]);
  const [feeParams, setFeeParams] = useState({
    sessions_per_week: 1, num_children: 1, child_number: 1,
    is_scc_member: false, is_section_member: false, is_digital_member: false,
    term_id: "", term_weeks: 10, include_registration_fee: false,
    include_ad_hoc: false, ad_hoc_sessions: 0, actual_paid: "",
    parent_name: "", child_name: ""
  });
  const [copiedRef, setCopiedRef] = useState(false);
  const feeQrRef = useRef(null);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingRes, allRes] = await Promise.all([
        axios.get(`${API}/payments/admin/pending-verifications`, { withCredentials: true }),
        axios.get(`${API}/payments/admin/all-transactions`, { withCredentials: true })
      ]);
      setPendingPayments(pendingRes.data);
      setAllPayments(allRes.data);
    } catch (error) {
      console.error("Error fetching payments:", error);
      toast.error("Failed to load payment data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTerms = async () => {
    try {
      const response = await axios.get(`${API}/academy/terms`, { withCredentials: true });
      setTerms(response.data || []);
    } catch (error) { console.error("Error fetching terms:", error); }
  };

  const fetchBankData = async () => {
    try {
      const [uploadsRes, entriesRes] = await Promise.all([
        axios.get(`${API}/payments/admin/bank-uploads`, { withCredentials: true }),
        axios.get(`${API}/payments/admin/bank-entries`, { withCredentials: true })
      ]);
      setBankUploads(uploadsRes.data || []);
      setBankEntries(entriesRes.data || []);
    } catch (error) { console.error("Error fetching bank data:", error); }
  };

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${API}/payments/admin/documents`, { withCredentials: true });
      setDocuments(res.data || []);
    } catch (error) { console.error("Error fetching documents:", error); }
  };

  const handleBatchGenerateReceipts = async () => {
    setGeneratingReceipts(true);
    try {
      const res = await axios.post(`${API}/payments/admin/batch-generate-receipts`, {}, { withCredentials: true });
      toast.success(`Generated ${res.data.generated} receipts (${res.data.skipped} already existed)`);
      await fetchDocuments();
    } catch (error) {
      toast.error("Failed to batch generate receipts");
    } finally { setGeneratingReceipts(false); }
  };

  const handlePreviewDocument = async (paymentId, docType) => {
    try {
      const res = await axios.get(`${API}/payments/admin/documents/${paymentId}/${docType}`, { withCredentials: true });
      setPreviewDoc(res.data);
    } catch (error) {
      toast.error("Failed to load document");
    }
  };

  const handleDownloadDocument = (paymentId, docType) => {
    window.open(`${API}/payments/admin/documents/${paymentId}/${docType}/html`, '_blank');
  };

  useEffect(() => { fetchPayments(); fetchTerms(); fetchBankData(); fetchDocuments(); }, [fetchPayments]);

  // Search parents
  const handleSearchParents = async () => {
    if (!parentSearch || parentSearch.length < 2) return;
    setSearchingParents(true);
    try {
      const res = await axios.post(`${API}/payments/admin/search-parents`, { query: parentSearch }, { withCredentials: true });
      setParentResults(res.data || []);
    } catch (error) { console.error("Error searching parents:", error); }
    finally { setSearchingParents(false); }
  };

  const handleSelectParent = (parent) => {
    setSelectedParent(parent);
    // Pre-fill children if parent has existing children
    if (parent.children?.length > 0) {
      setCreatePaymentChildren(parent.children.map(c => ({
        given_name: c.name?.split(" ")[0] || "",
        last_name: c.name?.split(" ").slice(1).join(" ") || "",
        birthdate: c.birthdate || "",
        gender: c.gender || "male",
        existing: true,
        id: c.id
      })));
    }
  };

  const [paymentPreview, setPaymentPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const buildCreatePayload = (overrides = {}) => {
    const parentEmail = selectedParent?.email || createPaymentNewParent.email;
    const parentName = selectedParent?.name || createPaymentNewParent.name;
    return {
      parent_email: parentEmail,
      parent_name: parentName,
      parent_phone: selectedParent?.phone || createPaymentNewParent.phone || "",
      children: createPaymentChildren,
      term_id: createPaymentTerm,
      weeks_to_bill: createPaymentWeeks,
      sessions_per_week: createPaymentSessions,
      is_scc_member: createPaymentIsMember,
      include_registration_fee: createPaymentRegFee,
      include_digital_membership: createPaymentDM,
      send_invoice: true,
      notes: createPaymentNotes,
      ...overrides,
    };
  };

  const validateCreateForm = () => {
    const parentEmail = selectedParent?.email || createPaymentNewParent.email;
    const parentName = selectedParent?.name || createPaymentNewParent.name;
    if (!parentEmail || !parentName) {
      toast.error("Parent name and email are required");
      return false;
    }
    if (!createPaymentTerm) {
      toast.error("Please select a term");
      return false;
    }
    if (createPaymentChildren.some(c => !c.given_name || !c.last_name)) {
      toast.error("Please fill in all children's names");
      return false;
    }
    const anyLineItem =
      createPaymentRegFee ||
      createPaymentDM ||
      (createPaymentWeeks > 0 && createPaymentSessions > 0);
    if (!anyLineItem) {
      toast.error("Please include at least one charge (sessions, registration fee, or DM)");
      return false;
    }
    return true;
  };

  const handlePreviewPayment = async () => {
    if (!validateCreateForm()) return;
    setPreviewLoading(true);
    try {
      const res = await axios.post(
        `${API}/payments/admin/create-payment`,
        buildCreatePayload({ preview_only: true }),
        { withCredentials: true }
      );
      setPaymentPreview(res.data);
    } catch (error) {
      { const __e = formatAdminError(error, "Failed to preview"); toast.error(__e.message, __e.opts); };
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCreatePayment = async () => {
    if (!validateCreateForm()) return;
    setCreatingPayment(true);
    try {
      const res = await axios.post(
        `${API}/payments/admin/create-payment`,
        buildCreatePayload(),
        { withCredentials: true }
      );

      toast.success(res.data.message, { duration: 8000 });
      setPaymentPreview(null);

      // Reset form
      setSelectedParent(null);
      setParentSearch("");
      setParentResults([]);
      setCreatePaymentChildren([{ given_name: "", last_name: "", birthdate: "", gender: "male" }]);
      setCreatePaymentNewParent({ name: "", email: "", phone: "" });
      setCreatePaymentNotes("");
      await fetchPayments();
      await fetchDocuments();
    } catch (error) {
      { const __e = formatAdminError(error, "Failed to create payment"); toast.error(__e.message, __e.opts); };
    } finally { setCreatingPayment(false); }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-SG", {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  const formatCurrency = (amount, currency = "SGD") => `$${parseFloat(amount || 0).toFixed(2)} ${currency?.toUpperCase() || "SGD"}`;

  const disputedCount = allPayments.filter(p => p.payment_status === "disputed").length;

  // ===== ACTION HANDLERS =====

  const handleEnhancedApprove = async () => {
    const payment = approveDialog.payment;
    if (!payment) return;
    setActionLoading(payment.id);
    try {
      const response = await axios.post(`${API}/payments/admin/approve/${payment.id}`, {
        payment_reference_number: approveData.bankRef || null,
        payee_bank_account_name: approveData.payeeName || null,
        approve_children: approveData.approveChildren,
        notes: approveData.notes || null
      }, { withCredentials: true });
      toast.success(response.data.message || "Payment approved!", { duration: 5000 });
      setApproveDialog({ open: false, payment: null });
      setApproveData({ bankRef: "", payeeName: "", approveChildren: true, notes: "" });
      await fetchPayments();
    } catch (error) {
      { const __e = formatAdminError(error, "Failed to approve payment"); toast.error(__e.message, __e.opts); };
    } finally { setActionLoading(null); }
  };

  const handleReject = async () => {
    const payment = rejectDialog.payment;
    if (!payment) return;
    setActionLoading(payment.id);
    try {
      await axios.post(`${API}/payments/paynow/verify/${payment.id}?action=reject`, {
        rejection_reason: rejectionReason
      }, { withCredentials: true });
      toast.success("Payment rejected. Parent notified.");
      setRejectDialog({ open: false, payment: null });
      setRejectionReason("");
      await fetchPayments();
    } catch (error) {
      { const __e = formatAdminError(error, "Failed to reject payment"); toast.error(__e.message, __e.opts); };
    } finally { setActionLoading(null); }
  };

  const handleDispute = async () => {
    const payment = disputeDialog.payment;
    if (!payment) return;
    setActionLoading(payment.id);
    try {
      await axios.post(`${API}/payments/admin/dispute/${payment.id}`, {
        dispute_reason: disputeData.reason,
        expected_amount: payment.amount,
        amount_received: disputeData.receivedAmount ? parseFloat(disputeData.receivedAmount) : null,
        send_email: true
      }, { withCredentials: true });
      toast.success("Dispute raised. Parent notified with verification query.");
      setDisputeDialog({ open: false, payment: null });
      setDisputeData({ reason: "", receivedAmount: "" });
      await fetchPayments();
    } catch (error) {
      { const __e = formatAdminError(error, "Failed to dispute payment"); toast.error(__e.message, __e.opts); };
    } finally { setActionLoading(null); }
  };

  const handleRevert = async () => {
    const payment = revertDialog.payment;
    if (!payment) return;
    setActionLoading(payment.id);
    try {
      await axios.post(`${API}/payments/admin/revert/${payment.id}`, {
        reason: revertReason || "Reverted for further review",
        send_email: true
      }, { withCredentials: true });
      toast.success("Payment reverted to pending verification.");
      setRevertDialog({ open: false, payment: null });
      setRevertReason("");
      await fetchPayments();
    } catch (error) {
      { const __e = formatAdminError(error, "Failed to revert payment"); toast.error(__e.message, __e.opts); };
    } finally { setActionLoading(null); }
  };

  // Feb 2026 — Mark as Refunded handler
  const handleRefund = async () => {
    const payment = refundDialog.payment;
    if (!payment) return;
    const reason = (refundReason || "").trim();
    if (!reason) {
      toast.error("A refund reason is required");
      return;
    }
    setActionLoading(payment.id);
    try {
      const res = await axios.post(`${API}/payments/admin/refund/${payment.id}`, {
        reason,
        send_email: true,
      }, { withCredentials: true });
      const flipMsg = res.data?.registration_flipped
        ? " Linked registration also flipped to refunded."
        : "";
      toast.success(`Payment marked as refunded.${flipMsg}`);
      setRefundDialog({ open: false, payment: null });
      setRefundReason("");
      await fetchPayments();
    } catch (error) {
      const __e = formatAdminError(error, "Failed to mark payment as refunded");
      toast.error(__e.message, __e.opts);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    const payment = deleteDialog.payment;
    if (!payment) return;
    setActionLoading(payment.id);
    try {
      await axios.delete(`${API}/payments/admin/${payment.id}`, { withCredentials: true });
      toast.success(`Payment ${payment.payment_reference || ""} deleted.`);
      setDeleteDialog({ open: false, payment: null });
      await fetchPayments();
    } catch (error) {
      { const __e = formatAdminError(error, "Failed to delete payment"); toast.error(__e.message, __e.opts); };
    } finally { setActionLoading(null); }
  };

  const handleSendReminder = async (paymentId) => {
    setActionLoading(paymentId);
    try {
      const res = await axios.post(`${API}/payments/admin/send-reminder/${paymentId}`, {}, { withCredentials: true });
      toast.success(res.data.message || "Reminder sent!");
    } catch (error) {
      { const __e = formatAdminError(error, "Failed to send reminder"); toast.error(__e.message, __e.opts); };
    } finally { setActionLoading(null); }
  };

  const handleResendInvoice = async (paymentId) => {
    setActionLoading(paymentId);
    try {
      const res = await axios.post(`${API}/payments/admin/resend-invoice/${paymentId}`, {}, { withCredentials: true });
      toast.success(res.data.message || "Invoice sent!");
    } catch (error) {
      { const __e = formatAdminError(error, "Failed to send invoice"); toast.error(__e.message, __e.opts); };
    } finally { setActionLoading(null); }
  };

  const handleResendReceipt = async (paymentId) => {
    setActionLoading(paymentId);
    try {
      const res = await axios.post(`${API}/payments/resend-receipt/${paymentId}`, {}, { withCredentials: true });
      toast.success(res.data.message || "Receipt sent!");
    } catch (error) {
      { const __e = formatAdminError(error, "Failed to resend receipt"); toast.error(__e.message, __e.opts); };
    } finally { setActionLoading(null); }
  };

  // Bank CSV upload
  const handleBankUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/payments/admin/upload-bank-csv`, formData, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`${res.data.message}`, { duration: 6000 });
      await fetchBankData();
    } catch (error) {
      { const __e = formatAdminError(error, "Failed to upload CSV"); toast.error(__e.message, __e.opts); };
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleManualLink = async () => {
    const entry = linkDialog.entry;
    if (!entry || !linkPaymentId) return;
    setActionLoading(entry.id);
    try {
      await axios.post(`${API}/payments/admin/link-bank-entry/${entry.id}`, {
        payment_transaction_id: linkPaymentId
      }, { withCredentials: true });
      toast.success("Bank entry linked to payment!");
      setLinkDialog({ open: false, entry: null });
      setLinkPaymentId("");
      await fetchBankData();
    } catch (error) {
      { const __e = formatAdminError(error, "Failed to link"); toast.error(__e.message, __e.opts); };
    } finally { setActionLoading(null); }
  };

  const handleUnlink = async (entryId) => {
    setActionLoading(entryId);
    try {
      await axios.post(`${API}/payments/admin/unlink-bank-entry/${entryId}`, {}, { withCredentials: true });
      toast.success("Bank entry unlinked.");
      await fetchBankData();
    } catch (error) {
      { const __e = formatAdminError(error, "Failed to unlink"); toast.error(__e.message, __e.opts); };
    } finally { setActionLoading(null); }
  };

  // Fee Calculator
  const calculateFee = async () => {
    try {
      setCalculatingFee(true);
      const response = await axios.post(`${API}/academy/admin/fee-calculator`, {
        ...feeParams, actual_paid: feeParams.actual_paid ? parseFloat(feeParams.actual_paid) : null
      }, { withCredentials: true });
      setFeeCalcResult(response.data);
      setShowFeeResult(true);
    } catch (error) {
      { const __e = formatAdminError(error, "Failed to calculate fee"); toast.error(__e.message, __e.opts); };
    } finally { setCalculatingFee(false); }
  };

  const downloadFeeQR = async () => {
    if (!feeQrRef.current) return;
    try {
      const canvas = await html2canvas(feeQrRef.current, { backgroundColor: "#1e293b", scale: 2 });
      const link = document.createElement("a");
      link.download = `fee-${feeCalcResult?.qr_data?.reference || "payment"}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("QR code downloaded!");
    } catch (error) { toast.error("Failed to download QR code"); }
  };

  const copyPaymentRef = () => {
    if (feeCalcResult?.qr_data?.reference) {
      navigator.clipboard.writeText(feeCalcResult.qr_data.reference);
      setCopiedRef(true);
      setTimeout(() => setCopiedRef(false), 2000);
      toast.success("Reference copied!");
    }
  };

  const prefillCalculatorFromPayment = (payment) => {
    const sessionsPerWeek = payment.fee_breakdown?.child_details?.reduce((acc, c) => acc + (c.session_count || 0), 0) || 1;
    const numChildren = payment.fee_breakdown?.child_details?.length || 1;
    const isSccMember = payment.fee_breakdown?.member_status?.is_member || false;
    setFeeParams({
      sessions_per_week: sessionsPerWeek, num_children: numChildren, child_number: 1,
      is_scc_member: isSccMember,
      is_section_member: payment.fee_breakdown?.member_status?.member_type === "section",
      is_digital_member: payment.include_digital_membership || false,
      term_id: payment.term_id || "", term_weeks: 10,
      include_registration_fee: payment.fee_breakdown?.registration_fee > 0,
      include_ad_hoc: false, ad_hoc_sessions: 0,
      actual_paid: payment.amount?.toString() || "",
      parent_name: payment.parent_name || "",
      child_name: payment.children_names?.join(", ") || payment.fee_breakdown?.child_details?.map(c => c.name).join(", ") || ""
    });
    setShowFeeCalculator(true);
  };

  // ===== CONTEXT-AWARE ACTION BUTTONS =====
  const getPaymentActions = (payment) => {
    const s = payment.payment_status;
    const isLoading = actionLoading === payment.id;
    const actions = [];

    // View always available
    actions.push(
      <Button key="view" size="sm" variant="outline" onClick={() => setSelectedPayment(payment)} data-testid={`view-payment-${payment.id}`}>
        <Eye className="w-3.5 h-3.5" />
      </Button>
    );

    // Calculator always available
    actions.push(
      <Button key="calc" size="sm" variant="outline" className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
        onClick={() => prefillCalculatorFromPayment(payment)} title="Fee Calculator" data-testid={`calc-payment-${payment.id}`}>
        <Calculator className="w-3.5 h-3.5" />
      </Button>
    );

    if (s === "pending_verification" || s === "disputed") {
      actions.push(
        <Button key="approve" size="sm" className="bg-green-600 hover:bg-green-700 text-white" disabled={isLoading}
          onClick={() => { setApproveDialog({ open: true, payment }); setApproveData({ bankRef: "", payeeName: payment.bank_account_holder_name || "", approveChildren: true, notes: "" }); }}
          data-testid={`approve-payment-${payment.id}`}>
          <CheckCircle className="w-3.5 h-3.5 mr-1" />Approve
        </Button>
      );
      actions.push(
        <Button key="dispute" size="sm" variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10" disabled={isLoading}
          onClick={() => setDisputeDialog({ open: true, payment })} data-testid={`dispute-payment-${payment.id}`}>
          <AlertTriangle className="w-3.5 h-3.5 mr-1" />Dispute
        </Button>
      );
      actions.push(
        <Button key="reject" size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10" disabled={isLoading}
          onClick={() => setRejectDialog({ open: true, payment })} data-testid={`reject-payment-${payment.id}`}>
          <XCircle className="w-3.5 h-3.5 mr-1" />Reject
        </Button>
      );
      // Send invoice/reminder
      actions.push(
        <Button key="invoice" size="sm" variant="outline" className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10" disabled={isLoading}
          onClick={() => handleResendInvoice(payment.id)} data-testid={`invoice-payment-${payment.id}`}>
          <FileText className="w-3.5 h-3.5 mr-1" />{isLoading ? "..." : "Invoice"}
        </Button>
      );
    }

    if (s === "paid" || s === "complete") {
      actions.push(
        <Button key="receipt" size="sm" variant="outline" className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10" disabled={isLoading}
          onClick={() => handleResendReceipt(payment.id)} data-testid={`receipt-payment-${payment.id}`}>
          <Mail className="w-3.5 h-3.5 mr-1" />{isLoading ? "..." : "Receipt"}
        </Button>
      );
      actions.push(
        <Button key="revert" size="sm" variant="outline" className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10" disabled={isLoading}
          onClick={() => setRevertDialog({ open: true, payment })} data-testid={`revert-payment-${payment.id}`}>
          <RotateCcw className="w-3.5 h-3.5 mr-1" />Revert
        </Button>
      );
      // Feb 2026 — Mark as Refunded for an approved/paid txn that needs
      // to be cleanly closed out (e.g. wrong amount, wrong parent).
      // Flips both the txn AND the linked registration to refunded.
      actions.push(
        <Button
          key="refund"
          size="sm"
          variant="outline"
          className="border-rose-500/50 text-rose-400 hover:bg-rose-500/10"
          disabled={isLoading}
          onClick={() => { setRefundReason(""); setRefundDialog({ open: true, payment }); }}
          data-testid={`refund-payment-${payment.id}`}
        >
          <XCircle className="w-3.5 h-3.5 mr-1" />Mark as Refunded
        </Button>
      );
    }

    if (s === "disputed") {
      actions.push(
        <Button key="remind" size="sm" variant="outline" className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10" disabled={isLoading}
          onClick={() => handleSendReminder(payment.id)} data-testid={`remind-payment-${payment.id}`}>
          <Send className="w-3.5 h-3.5 mr-1" />{isLoading ? "..." : "Remind"}
        </Button>
      );
    }

    if (s === "rejected" || s === "unpaid" || s === "initiated" || s === "expired") {
      actions.push(
        <Button key="delete" size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10" disabled={isLoading}
          onClick={() => setDeleteDialog({ open: true, payment })} data-testid={`delete-payment-${payment.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      );
    }

    return actions;
  };

  // ===== RENDER =====

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading payment data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5 shadow-lg">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/academy">
              <Button variant="ghost" className="text-white hover:text-blue-200" data-testid="back-to-academy-btn">
                <ArrowLeft className="w-5 h-5 mr-2" />Back to Academy
              </Button>
            </Link>
            <h1 className="text-xl font-bold text-white">Payment Management</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setShowFeeCalculator(true)} className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700" size="sm" data-testid="fee-calculator-btn">
              <Calculator className="w-4 h-4 mr-2" />Fee Calculator
            </Button>
            <Button onClick={() => { fetchPayments(); fetchBankData(); }} variant="outline" size="sm" className="border-white/20 text-white hover:bg-slate-900/50" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />Refresh
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <SummaryCards pendingPayments={pendingPayments} allPayments={allPayments} disputedCount={disputedCount} />

        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="mb-6" data-testid="payment-tabs">
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pending {pendingPayments.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-yellow-500 text-white rounded-full">{pendingPayments.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">All Transactions</TabsTrigger>
            <TabsTrigger value="bank" data-testid="tab-bank">
              Bank Uploads {bankEntries.filter(e => !e.is_matched).length > 0 && <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-orange-500 text-white rounded-full">{bankEntries.filter(e => !e.is_matched).length}</span>}
            </TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">
              Invoices & Receipts {documents.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-blue-500 text-white rounded-full">{documents.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="admin-actions" data-testid="tab-admin-actions">
              Create Payment
            </TabsTrigger>
            <TabsTrigger value="team-assignment" data-testid="tab-team-assignment">
              Team Assignment
            </TabsTrigger>
            <TabsTrigger value="coach-assignment" data-testid="tab-coach-assignment">
              Coach Assignment
            </TabsTrigger>
            <TabsTrigger value="attendance-overview" data-testid="tab-attendance-overview">
              Attendance
            </TabsTrigger>
          </TabsList>

          {/* ===== PENDING TAB ===== */}
          <TabsContent value="pending">
            <Card className="p-6 bg-slate-900/50 shadow-lg" data-testid="pending-payments-card">
              {pendingPayments.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">All Caught Up!</h3>
                  <p className="text-slate-400">No payments pending verification</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Reference</TableHead>
                        <TableHead>Parent / Bank Name</TableHead>
                        <TableHead>Context</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingPayments.map((payment) => (
                        <TableRow key={payment.id} className="hover:bg-slate-800/30">
                          <TableCell>
                            <p className="font-mono font-bold text-blue-400 text-sm">{payment.payment_reference}</p>
                            <p className="text-xs text-slate-500">{payment.payment_type}</p>
                          </TableCell>
                          <TableCell>
                            <p className="font-medium text-sm">{payment.parent_name || "N/A"}</p>
                            <p className="text-xs text-slate-400">{payment.parent_email}</p>
                            {payment.bank_account_holder_name && (
                              <p className="text-xs text-emerald-400 font-medium mt-1">Bank: {payment.bank_account_holder_name}</p>
                            )}
                            {payment.member_verification_required && (
                              <div className="mt-1 p-1.5 rounded bg-amber-500/10 border border-amber-500/30" data-testid="member-verification-badge">
                                <p className="text-[11px] text-amber-300 font-semibold">⚠ Verify SCC member claim</p>
                                <p className="text-[10px] text-amber-200/80">
                                  #{payment.membership_number}
                                  {payment.primary_member_name && ` · Primary: ${payment.primary_member_name}`}
                                  {payment.relationship_to_primary_member && ` (${payment.relationship_to_primary_member})`}
                                </p>
                              </div>
                            )}
                            {payment.reg_fee_waiver_count > 0 && (
                              <div className="mt-1 p-1.5 rounded bg-orange-500/10 border border-orange-500/30" data-testid="reg-fee-waiver-badge">
                                <p className="text-[11px] text-orange-300 font-semibold">⚠ Verify registration fee waiver</p>
                                <p className="text-[10px] text-orange-200/80">
                                  Parent claims reg fee already paid for: {payment.reg_fee_waived_children?.join(", ")}
                                </p>
                              </div>
                            )}
                            <ChildrenInfo payment={payment} />
                          </TableCell>
                          <TableCell><RegistrationContext payment={payment} /></TableCell>
                          <TableCell>
                            <span className="font-semibold text-emerald-400 text-sm">{formatCurrency(payment.amount, payment.currency)}</span>
                          </TableCell>
                          <TableCell>
                            <p className="text-xs">{formatDate(payment.created_at)}</p>
                            {payment.customer_confirmed_at && <p className="text-xs text-yellow-500">Confirmed: {formatDate(payment.customer_confirmed_at)}</p>}
                          </TableCell>
                          <TableCell><StatusBadge status={payment.payment_status} /></TableCell>
                          <TableCell className="text-right">
                            <div className="flex flex-wrap justify-end gap-1">{getPaymentActions(payment)}</div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* ===== ALL TRANSACTIONS TAB ===== */}
          <TabsContent value="all">
            <Card className="p-6 bg-slate-900/50 shadow-lg" data-testid="all-payments-card">
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <Input placeholder="Search name, email, reference..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-slate-800/50 border-slate-700" data-testid="payment-search-input" />
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {[
                    { key: "all", label: "All", count: allPayments.length },
                    { key: "paid", label: "Paid", count: allPayments.filter(p => p.payment_status === "paid" || p.payment_status === "complete").length, color: "emerald" },
                    { key: "pending", label: "Pending", count: allPayments.filter(p => p.payment_status === "pending_verification").length, color: "yellow" },
                    { key: "disputed", label: "Disputed", count: disputedCount, color: "amber" },
                    { key: "rejected", label: "Unpaid", count: allPayments.filter(p => p.payment_status === "rejected" || p.payment_status === "unpaid").length, color: "red" },
                  ].map(f => (
                    <Button key={f.key} variant={statusFilter === f.key ? "default" : "outline"} size="sm"
                      onClick={() => setStatusFilter(f.key)}
                      className={statusFilter === f.key ? (f.color ? `bg-${f.color}-600` : "bg-blue-600") : `border-${f.color || "blue"}-500/50 text-${f.color || "blue"}-400`}
                      data-testid={`filter-${f.key}`}>
                      {f.label} ({f.count})
                    </Button>
                  ))}
                </div>
              </div>

              {(() => {
                let filtered = allPayments;
                if (statusFilter === "paid") filtered = filtered.filter(p => p.payment_status === "paid" || p.payment_status === "complete");
                else if (statusFilter === "pending") filtered = filtered.filter(p => p.payment_status === "pending_verification");
                else if (statusFilter === "disputed") filtered = filtered.filter(p => p.payment_status === "disputed");
                else if (statusFilter === "rejected") filtered = filtered.filter(p => p.payment_status === "rejected" || p.payment_status === "unpaid");

                if (searchTerm.trim()) {
                  const term = searchTerm.toLowerCase();
                  filtered = filtered.filter(p =>
                    (p.parent_name || "").toLowerCase().includes(term) ||
                    (p.parent_email || "").toLowerCase().includes(term) ||
                    (p.payment_reference || "").toLowerCase().includes(term) ||
                    (p.bank_account_holder_name || "").toLowerCase().includes(term) ||
                    (p.payee_bank_account_name || "").toLowerCase().includes(term) ||
                    (p.fee_breakdown?.child_details?.some(c => c.name?.toLowerCase().includes(term)))
                  );
                }

                if (filtered.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <CreditCard className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-white mb-2">No Transactions Found</h3>
                      {(statusFilter !== "all" || searchTerm) && (
                        <Button variant="outline" className="mt-4" onClick={() => { setStatusFilter("all"); setSearchTerm(""); }}>Clear Filters</Button>
                      )}
                    </div>
                  );
                }

                return (
                  <div className="overflow-x-auto">
                    <p className="text-sm text-slate-400 mb-3">Showing {filtered.length} of {allPayments.length} transactions</p>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Reference</TableHead>
                          <TableHead>Parent</TableHead>
                          <TableHead>Context</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map((payment) => (
                          <TableRow key={payment.id} className="hover:bg-slate-800/30">
                            <TableCell>
                              <p className="font-mono font-bold text-blue-400 text-sm">{payment.payment_reference}</p>
                              <p className="text-xs text-slate-500">{payment.payment_type}</p>
                            </TableCell>
                            <TableCell>
                              <p className="font-medium text-sm">{payment.parent_name || payment.user_name || "N/A"}</p>
                              <p className="text-xs text-slate-400">{payment.parent_email || payment.user_email}</p>
                              {payment.payee_bank_account_name && <p className="text-xs text-emerald-400 mt-0.5">Payee: {payment.payee_bank_account_name}</p>}
                              {payment.bank_reference_number && <p className="text-xs text-cyan-400 mt-0.5">Bank Ref: {payment.bank_reference_number}</p>}
                              <ChildrenInfo payment={payment} />
                            </TableCell>
                            <TableCell><RegistrationContext payment={payment} /></TableCell>
                            <TableCell><MethodBadge method={payment.payment_method} /></TableCell>
                            <TableCell><span className="font-semibold text-emerald-400 text-sm">{formatCurrency(payment.amount, payment.currency)}</span></TableCell>
                            <TableCell><p className="text-xs">{formatDate(payment.created_at)}</p></TableCell>
                            <TableCell><StatusBadge status={payment.payment_status} /></TableCell>
                            <TableCell className="text-right">
                              <div className="flex flex-wrap justify-end gap-1">{getPaymentActions(payment)}</div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                );
              })()}
            </Card>
          </TabsContent>

          {/* ===== BANK UPLOADS TAB ===== */}
          <TabsContent value="bank">
            <Card className="p-6 bg-slate-900/50 shadow-lg" data-testid="bank-uploads-card">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-white">Bank Payment Records</h3>
                  <p className="text-sm text-slate-400">Upload CSV from your bank to match against payment transactions</p>
                </div>
                <div className="flex gap-2">
                  <input type="file" ref={fileInputRef} accept=".csv" onChange={handleBankUpload} className="hidden" />
                  <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700" data-testid="upload-bank-csv-btn">
                    <Upload className="w-4 h-4 mr-2" />{uploading ? "Uploading..." : "Upload Bank CSV"}
                  </Button>
                </div>
              </div>

              {/* Upload history */}
              {bankUploads.length > 0 && (
                <div className="mb-6 space-y-2">
                  <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">Upload History</p>
                  <div className="flex flex-wrap gap-2">
                    {bankUploads.map(upload => (
                      <div key={upload.id} className="p-2 bg-slate-800/50 rounded-lg border border-slate-700 text-xs">
                        <p className="text-white font-medium">{upload.filename}</p>
                        <p className="text-slate-400">{formatDate(upload.uploaded_at)} | {upload.total_entries} entries | {upload.matched_entries} matched</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Filter */}
              <div className="flex gap-2 mb-4">
                {[
                  { key: "all", label: "All", count: bankEntries.length },
                  { key: "matched", label: "Matched", count: bankEntries.filter(e => e.is_matched).length },
                  { key: "unmatched", label: "Unmatched", count: bankEntries.filter(e => !e.is_matched).length },
                ].map(f => (
                  <Button key={f.key} variant={bankFilter === f.key ? "default" : "outline"} size="sm"
                    onClick={() => setBankFilter(f.key)} data-testid={`bank-filter-${f.key}`}>
                    {f.label} ({f.count})
                  </Button>
                ))}
              </div>

              {bankEntries.length === 0 ? (
                <div className="text-center py-12">
                  <Upload className="w-16 h-16 text-slate-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">No Bank Records</h3>
                  <p className="text-slate-400">Upload a CSV export from your bank to get started</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Source (Payer)</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Bank Ref</TableHead>
                        <TableHead>Match Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bankEntries
                        .filter(e => bankFilter === "all" || (bankFilter === "matched" ? e.is_matched : !e.is_matched))
                        .map((entry) => (
                          <TableRow key={entry.id} className={`hover:bg-slate-800/30 ${entry.is_matched ? "" : "bg-orange-500/5"}`}>
                            <TableCell className="text-xs">{entry.created_on || entry.finished_on || "N/A"}</TableCell>
                            <TableCell>
                              <p className="font-medium text-sm">{entry.source_name || "N/A"}</p>
                              {entry.note && <p className="text-xs text-slate-400">{entry.note}</p>}
                            </TableCell>
                            <TableCell>
                              <span className="font-semibold text-emerald-400">${(entry.source_amount || entry.target_amount || 0).toFixed(2)}</span>
                              <span className="text-xs text-slate-500 ml-1">{entry.source_currency || "SGD"}</span>
                            </TableCell>
                            <TableCell><span className="text-xs font-mono text-slate-400">{entry.bank_transaction_id || "-"}</span></TableCell>
                            <TableCell>
                              {entry.is_matched ? (
                                <div>
                                  <Badge variant="outline" className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">
                                    <Link2 className="w-3 h-3 mr-1" />Matched
                                  </Badge>
                                  <p className="text-xs text-slate-400 mt-1">{entry.matched_payment_ref}</p>
                                  <p className="text-xs text-slate-500">{entry.matched_parent_name}</p>
                                  {entry.match_confidence && (
                                    <span className={`text-xs px-1 rounded mt-0.5 inline-block ${
                                      entry.match_confidence === "high" ? "bg-green-500/20 text-green-400" :
                                      entry.match_confidence === "medium" ? "bg-yellow-500/20 text-yellow-400" :
                                      entry.match_confidence === "manual" ? "bg-blue-500/20 text-blue-400" :
                                      "bg-orange-500/20 text-orange-400"
                                    }`}>{entry.match_confidence} ({entry.match_method})</span>
                                  )}
                                </div>
                              ) : (
                                <Badge variant="outline" className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs">
                                  <Ban className="w-3 h-3 mr-1" />Unmatched
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                {!entry.is_matched && (
                                  <Button size="sm" variant="outline" className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                                    onClick={() => { setLinkDialog({ open: true, entry }); setLinkPaymentId(""); }}
                                    data-testid={`link-bank-${entry.id}`}>
                                    <Link2 className="w-3.5 h-3.5 mr-1" />Link
                                  </Button>
                                )}
                                {entry.is_matched && (
                                  <Button size="sm" variant="outline" className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                                    onClick={() => handleUnlink(entry.id)} disabled={actionLoading === entry.id}
                                    data-testid={`unlink-bank-${entry.id}`}>
                                    <Unlink className="w-3.5 h-3.5 mr-1" />Unlink
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* ===== DOCUMENTS TAB ===== */}
          <TabsContent value="documents">
            <Card className="p-6 bg-slate-900/50 shadow-lg" data-testid="documents-card">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-white">Invoices & Receipts</h3>
                  <p className="text-sm text-slate-400">View and download all generated invoices and receipts</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleBatchGenerateReceipts} disabled={generatingReceipts}
                    className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700" size="sm" data-testid="batch-generate-btn">
                    <FileText className="w-4 h-4 mr-2" />{generatingReceipts ? "Generating..." : "Generate Missing Receipts"}
                  </Button>
                  <Button onClick={fetchDocuments} variant="outline" size="sm" className="border-white/20 text-white hover:bg-slate-900/50">
                    <RefreshCw className="w-4 h-4 mr-2" />Refresh
                  </Button>
                </div>
              </div>

              {/* Filter */}
              <div className="flex gap-2 mb-4">
                {[
                  { key: "all", label: "All", count: documents.length },
                  { key: "invoice", label: "Invoices", count: documents.filter(d => d.doc_type === "invoice").length },
                  { key: "receipt", label: "Receipts", count: documents.filter(d => d.doc_type === "receipt").length },
                ].map(f => (
                  <Button key={f.key} variant={docFilter === f.key ? "default" : "outline"} size="sm"
                    onClick={() => setDocFilter(f.key)} data-testid={`doc-filter-${f.key}`}>
                    {f.label} ({f.count})
                  </Button>
                ))}
              </div>

              {documents.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-slate-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">No Documents Yet</h3>
                  <p className="text-slate-400 mb-4">Documents are generated when invoices are sent or payments are confirmed.</p>
                  <Button onClick={handleBatchGenerateReceipts} disabled={generatingReceipts}
                    className="bg-emerald-600 hover:bg-emerald-700">
                    Generate Receipts for Paid Transactions
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead>Parent</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Generated</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {documents
                        .filter(d => docFilter === "all" || d.doc_type === docFilter)
                        .map((doc) => (
                          <TableRow key={`${doc.payment_id}-${doc.doc_type}`} className="hover:bg-slate-800/30">
                            <TableCell>
                              <Badge variant="outline" className={doc.doc_type === "invoice"
                                ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
                                : "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"}>
                                {doc.doc_type === "invoice" ? "Invoice" : "Receipt"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="font-mono font-bold text-blue-400 text-sm">{doc.payment_id}</span>
                            </TableCell>
                            <TableCell>
                              <p className="font-medium text-sm">{doc.parent_name || "N/A"}</p>
                              <p className="text-xs text-slate-400">{doc.to_email}</p>
                            </TableCell>
                            <TableCell>
                              <span className="font-semibold text-emerald-400">${(doc.amount || 0).toFixed(2)}</span>
                            </TableCell>
                            <TableCell>
                              <p className="text-xs">{formatDate(doc.generated_at)}</p>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button size="sm" variant="outline" className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                                  onClick={() => handlePreviewDocument(doc.payment_id, doc.doc_type)}
                                  data-testid={`preview-doc-${doc.payment_id}`}>
                                  <Eye className="w-3.5 h-3.5 mr-1" />View
                                </Button>
                                <Button size="sm" variant="outline" className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                                  onClick={() => handleDownloadDocument(doc.payment_id, doc.doc_type)}
                                  data-testid={`download-doc-${doc.payment_id}`}>
                                  <Download className="w-3.5 h-3.5 mr-1" />Open
                                </Button>
                                {doc.doc_type === "invoice" && (
                                  <Button size="sm" variant="outline" className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                                    onClick={() => window.open(`${API}/payments/admin/payment-slip-pdf/${doc.payment_id}`, '_blank')}
                                    data-testid={`download-slip-${doc.payment_id}`}>
                                    <Download className="w-3.5 h-3.5 mr-1" />PDF Slip
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* ===== ADMIN ACTIONS TAB ===== */}
          <TabsContent value="admin-actions">
            <Card className="p-6 bg-slate-900/50 shadow-lg" data-testid="admin-actions-card">
              <h3 className="text-lg font-semibold text-white mb-2">Create Payment for Parent</h3>
              <p className="text-sm text-slate-400 mb-6">Search or create a parent, add children, select term and weeks, generate invoice with QR code.</p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* LEFT: Parent & Children */}
                <div className="space-y-4">
                  {/* Step 1: Find/Create Parent */}
                  <Card className="p-4 bg-[#0a0f1a] border-slate-700">
                    <h4 className="font-semibold text-blue-400 mb-3">1. Parent</h4>
                    {!selectedParent ? (
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <Input placeholder="Search by name or email..." value={parentSearch}
                            onChange={(e) => setParentSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearchParents()}
                            className="bg-slate-800/50 border-slate-700" data-testid="parent-search-input" />
                          <Button onClick={handleSearchParents} disabled={searchingParents} size="sm" className="bg-blue-600">
                            {searchingParents ? "..." : "Search"}
                          </Button>
                        </div>
                        {parentResults.length > 0 && (
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {parentResults.map(p => (
                              <div key={p.id} className="p-2 bg-slate-800/50 rounded border border-slate-700 cursor-pointer hover:border-blue-500 transition-colors"
                                onClick={() => handleSelectParent(p)}>
                                <p className="font-medium text-sm text-white">{p.name}</p>
                                <p className="text-xs text-slate-400">{p.email} {p.children?.length > 0 && `| ${p.children.length} child(ren)`}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="border-t border-slate-700 pt-3">
                          <p className="text-xs text-slate-400 mb-2">Or create new parent:</p>
                          <div className="grid grid-cols-2 gap-2">
                            <Input placeholder="Full Name *" value={createPaymentNewParent.name}
                              onChange={(e) => setCreatePaymentNewParent(p => ({ ...p, name: e.target.value }))}
                              className="bg-slate-800/50 border-slate-700 text-sm" />
                            <Input placeholder="Email *" value={createPaymentNewParent.email}
                              onChange={(e) => setCreatePaymentNewParent(p => ({ ...p, email: e.target.value }))}
                              className="bg-slate-800/50 border-slate-700 text-sm" />
                          </div>
                          <Input placeholder="Phone" value={createPaymentNewParent.phone}
                            onChange={(e) => setCreatePaymentNewParent(p => ({ ...p, phone: e.target.value }))}
                            className="bg-slate-800/50 border-slate-700 text-sm mt-2" />
                          {createPaymentNewParent.email && (
                            <Button size="sm" className="mt-2 bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => setSelectedParent({ name: createPaymentNewParent.name, email: createPaymentNewParent.email, phone: createPaymentNewParent.phone, isNew: true })}>
                              Use This Parent
                            </Button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                        <div>
                          <p className="font-medium text-emerald-400">{selectedParent.name}</p>
                          <p className="text-xs text-slate-400">{selectedParent.email}</p>
                          {selectedParent.isNew && <Badge className="bg-blue-500/20 text-blue-400 text-xs mt-1">New Account</Badge>}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedParent(null); setParentResults([]); setParentSearch(""); }}
                          className="text-slate-400">Change</Button>
                      </div>
                    )}
                  </Card>

                  {/* Step 2: Children */}
                  <Card className="p-4 bg-[#0a0f1a] border-slate-700">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-blue-400">2. Children</h4>
                      <Button size="sm" variant="outline" className="border-blue-500/50 text-blue-400"
                        onClick={() => setCreatePaymentChildren(prev => [...prev, { given_name: "", last_name: "", birthdate: "", gender: "male" }])}>
                        <Plus className="w-3 h-3 mr-1" />Add Child
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {createPaymentChildren.map((child, idx) => (
                        <div key={idx} className="p-3 bg-slate-800/30 rounded border border-slate-700">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-slate-400">Child {idx + 1} {child.existing && <Badge className="bg-emerald-500/20 text-emerald-400 text-xs ml-1">Existing</Badge>}</span>
                            {createPaymentChildren.length > 1 && (
                              <Button variant="ghost" size="sm" className="text-red-400 h-6 px-1"
                                onClick={() => setCreatePaymentChildren(prev => prev.filter((_, i) => i !== idx))}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                          {child.existing ? (
                            <p className="text-sm font-medium text-white">{child.given_name} {child.last_name}</p>
                          ) : (
                            <div className="grid grid-cols-2 gap-2">
                              <Input placeholder="Given Name *" value={child.given_name}
                                onChange={(e) => { const updated = [...createPaymentChildren]; updated[idx].given_name = e.target.value; setCreatePaymentChildren(updated); }}
                                className="bg-slate-800/50 border-slate-600 text-sm" />
                              <Input placeholder="Last Name *" value={child.last_name}
                                onChange={(e) => { const updated = [...createPaymentChildren]; updated[idx].last_name = e.target.value; setCreatePaymentChildren(updated); }}
                                className="bg-slate-800/50 border-slate-600 text-sm" />
                              <Input type="date" value={child.birthdate}
                                onChange={(e) => { const updated = [...createPaymentChildren]; updated[idx].birthdate = e.target.value; setCreatePaymentChildren(updated); }}
                                className="bg-slate-800/50 border-slate-600 text-sm" />
                              <select value={child.gender}
                                onChange={(e) => { const updated = [...createPaymentChildren]; updated[idx].gender = e.target.value; setCreatePaymentChildren(updated); }}
                                className="border rounded-md px-2 py-1.5 bg-slate-800 border-slate-600 text-white text-sm">
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                              </select>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>

                {/* RIGHT: Term, Fees, Actions */}
                <div className="space-y-4">
                  {/* Step 3: Term & Billing */}
                  <Card className="p-4 bg-[#0a0f1a] border-slate-700">
                    <h4 className="font-semibold text-blue-400 mb-3">3. Term & Billing</h4>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-slate-300 text-sm">Select Term</Label>
                        <Select value={createPaymentTerm} onValueChange={setCreatePaymentTerm}>
                          <SelectTrigger className="bg-slate-800/50 border-slate-700" data-testid="create-payment-term">
                            <SelectValue placeholder="Choose term..." />
                          </SelectTrigger>
                          <SelectContent>
                            {terms.map(t => <SelectItem key={t.id} value={t.id}>{t.name} ({t.start_date?.slice(0,10)})</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-slate-300 text-sm">Weeks to Bill</Label>
                          <Input type="number" min={0} max={52} value={createPaymentWeeks}
                            onChange={(e) => {
                              const v = e.target.value;
                              setCreatePaymentWeeks(v === "" ? 0 : parseInt(v) || 0);
                            }}
                            className="bg-slate-800/50 border-slate-700" data-testid="weeks-to-bill" />
                          <p className="text-[10px] text-slate-500 mt-1">Set to 0 if billing only for registration fee / DM</p>
                        </div>
                        <div>
                          <Label className="text-slate-300 text-sm">Sessions/Week</Label>
                          <Input type="number" min={0} max={7} value={createPaymentSessions}
                            onChange={(e) => {
                              const v = e.target.value;
                              setCreatePaymentSessions(v === "" ? 0 : parseInt(v) || 0);
                            }}
                            className="bg-slate-800/50 border-slate-700" data-testid="sessions-per-week" />
                          <p className="text-[10px] text-slate-500 mt-1">Set to 0 for non-session charges</p>
                        </div>
                      </div>
                      {/* Quick preset for the most common "forgot registration fee" scenario */}
                      <button
                        type="button"
                        onClick={() => {
                          setCreatePaymentWeeks(0);
                          setCreatePaymentSessions(0);
                          setCreatePaymentRegFee(true);
                          setCreatePaymentDM(false);
                        }}
                        className="text-xs text-emerald-400 hover:text-emerald-300 underline"
                        data-testid="reg-fee-only-preset"
                      >
                        📋 Registration fee only (skip sessions)
                      </button>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox id="cp-member" checked={createPaymentIsMember}
                            onCheckedChange={(c) => setCreatePaymentIsMember(c)} />
                          <Label htmlFor="cp-member" className="text-slate-300 text-sm cursor-pointer">SCC Member</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox id="cp-reg" checked={createPaymentRegFee}
                            onCheckedChange={(c) => setCreatePaymentRegFee(c)} />
                          <Label htmlFor="cp-reg" className="text-slate-300 text-sm cursor-pointer">Include Registration Fee</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox id="cp-dm" checked={createPaymentDM}
                            onCheckedChange={(c) => setCreatePaymentDM(c)} />
                          <Label htmlFor="cp-dm" className="text-slate-300 text-sm cursor-pointer">Include Digital Membership</Label>
                        </div>
                      </div>
                      <div>
                        <Label className="text-slate-300 text-sm">Admin Notes</Label>
                        <Input placeholder="e.g., Late registration for Term 1" value={createPaymentNotes}
                          onChange={(e) => setCreatePaymentNotes(e.target.value)}
                          className="bg-slate-800/50 border-slate-700" />
                      </div>
                    </div>
                  </Card>

                  {/* Step 4: Preview then Create */}
                  <Button
                    onClick={handlePreviewPayment}
                    disabled={previewLoading || creatingPayment}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 py-5 text-base"
                    data-testid="preview-payment-btn"
                  >
                    {previewLoading ? (
                      <><RefreshCw className="w-5 h-5 mr-2 animate-spin" />Building Preview...</>
                    ) : (
                      <><Eye className="w-5 h-5 mr-2" />Preview Invoice</>
                    )}
                  </Button>
                  <p className="text-xs text-slate-400 text-center">
                    Nothing is charged or emailed until you click <b>Send</b> on the preview.
                  </p>

                  {/* Preview dialog */}
                  <Dialog open={!!paymentPreview} onOpenChange={(open) => !open && setPaymentPreview(null)}>
                    <DialogContent className="bg-slate-900 border-slate-700 max-w-lg" data-testid="payment-preview-dialog">
                      <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                          <FileText className="w-5 h-5 text-emerald-400" />
                          Invoice preview — review before sending
                        </DialogTitle>
                      </DialogHeader>
                      {paymentPreview && (
                        <div className="space-y-3 text-slate-200 py-2">
                          <div className="text-sm bg-slate-800/60 p-3 rounded">
                            <p><span className="text-slate-400">Parent:</span> {paymentPreview.parent_name} <span className="text-slate-500">&lt;{paymentPreview.parent_email}&gt;</span></p>
                            <p><span className="text-slate-400">Term:</span> {paymentPreview.term_name || "—"}</p>
                            <p><span className="text-slate-400">Children:</span> {paymentPreview.children_count}</p>
                          </div>
                          <div className="bg-slate-800/60 p-3 rounded text-sm">
                            <p className="font-semibold text-white mb-2">Line items</p>
                            {paymentPreview.line_items.length === 0 ? (
                              <p className="text-slate-500 italic">No line items — nothing to charge.</p>
                            ) : (
                              paymentPreview.line_items.map((li, i) => (
                                <div key={i} className="flex justify-between py-0.5">
                                  <span className="text-slate-300 flex-1 pr-2">{li.label}</span>
                                  <span className={`font-mono ${li.amount < 0 ? "text-rose-300" : "text-white"}`}>{li.amount < 0 ? "-" : ""}${Math.abs(li.amount).toFixed(2)}</span>
                                </div>
                              ))
                            )}
                            <div className="border-t border-slate-700 mt-2 pt-2 flex justify-between items-center">
                              <span className="font-semibold text-emerald-300">Total to charge</span>
                              <span className="font-mono font-bold text-emerald-300 text-lg">${paymentPreview.total.toFixed(2)}</span>
                            </div>
                          </div>
                          {paymentPreview.will_send_invoice_to && (
                            <p className="text-xs text-slate-400">📧 Email with PayNow QR will be sent to <b>{paymentPreview.will_send_invoice_to}</b></p>
                          )}
                        </div>
                      )}
                      <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setPaymentPreview(null)} className="border-slate-700" data-testid="preview-edit-btn">
                          Edit
                        </Button>
                        <Button
                          onClick={handleCreatePayment}
                          disabled={creatingPayment || !paymentPreview || paymentPreview.total <= 0}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="preview-send-btn"
                        >
                          {creatingPayment ? (
                            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                          ) : (
                            <><Send className="w-4 h-4 mr-2" />Send invoice now</>
                          )}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* ===== TEAM ASSIGNMENT TAB ===== */}
          <TabsContent value="team-assignment">
            <Card className="p-6 bg-slate-900/50 shadow-lg" data-testid="team-assignment-card">
              <TeamAssignmentPanel />
            </Card>
          </TabsContent>

          {/* ===== COACH ASSIGNMENT TAB ===== */}
          <TabsContent value="coach-assignment">
            <Card className="p-6 bg-slate-900/50 shadow-lg" data-testid="coach-assignment-card">
              <CoachAssignmentPanel />
            </Card>
          </TabsContent>

          {/* ===== ATTENDANCE OVERVIEW TAB ===== */}
          <TabsContent value="attendance-overview">
            <Card className="p-6 bg-slate-900/50 shadow-lg" data-testid="attendance-overview-card">
              <AttendanceOverviewPanel />
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* ===== PAYMENT DETAILS DIALOG ===== */}
      <Dialog open={!!selectedPayment} onOpenChange={() => setSelectedPayment(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payment Details</DialogTitle>
            <DialogDescription>Reference: {selectedPayment?.payment_reference}</DialogDescription>
          </DialogHeader>
          {selectedPayment && (
            <div className="space-y-4">
              {/* Registration Context */}
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-sm text-blue-400 font-bold mb-3">Payment Summary</p>
                {(selectedPayment.fee_breakdown?.term_name || selectedPayment.term_id) && (
                  <div className="mb-3 p-2 bg-slate-800/50 rounded">
                    <p className="text-xs text-slate-400">Term</p>
                    <p className="font-bold text-white">{selectedPayment.fee_breakdown?.term_name || "Term Enrollment"}</p>
                  </div>
                )}
                <div className={`mb-3 p-2 rounded ${selectedPayment.fee_breakdown?.member_status?.is_member ? 'bg-emerald-500/20' : 'bg-orange-500/20'}`}>
                  <p className="text-xs text-slate-400">Member Status</p>
                  <p className={`font-bold ${selectedPayment.fee_breakdown?.member_status?.is_member ? 'text-emerald-400' : 'text-orange-400'}`}>
                    {selectedPayment.fee_breakdown?.member_status?.is_member ? 'MEMBER' : 'NON-MEMBER'}
                  </p>
                </div>
                {selectedPayment.fee_breakdown?.registration_fee > 0 && (
                  <Badge className="bg-cyan-500/20 text-cyan-400 text-xs mb-2">New Registration (fee included)</Badge>
                )}
                {selectedPayment.include_digital_membership && (
                  <Badge className="bg-purple-500/20 text-purple-400 text-xs mb-2 ml-1">Digital Membership</Badge>
                )}
                {/* Children Details */}
                <div className="space-y-2 mt-3">
                  <p className="text-sm font-medium text-white">Children & Sessions:</p>
                  {selectedPayment.fee_breakdown?.child_details?.length > 0 ? (
                    selectedPayment.fee_breakdown.child_details.map((child, idx) => (
                      <div key={idx} className="p-3 bg-slate-800 rounded-lg border border-slate-700">
                        <div className="flex justify-between items-start mb-1">
                          <p className="font-bold text-white text-sm">{child.name}</p>
                          <p className="font-bold text-emerald-400 text-sm">${(child.term_fee || 0).toFixed(2)}</p>
                        </div>
                        <p className="text-xs text-slate-400">{child.session_count} session(s)/week</p>
                        {child.sessions?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {child.sessions.map((sess, sIdx) => (
                              <span key={sIdx} className="text-xs bg-slate-700 px-2 py-0.5 rounded">{sess.day} {sess.time}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  ) : selectedPayment.children_names?.length > 0 ? (
                    <p className="text-sm text-white">{selectedPayment.children_names.join(", ")}</p>
                  ) : null}
                </div>
              </div>

              {/* Customer & Amount */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Customer</p>
                  <p className="font-medium text-sm">{selectedPayment.parent_name || selectedPayment.user_name}</p>
                  <p className="text-xs text-slate-400">{selectedPayment.parent_email || selectedPayment.user_email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Total Amount</p>
                  <p className="text-2xl font-bold text-emerald-400">{formatCurrency(selectedPayment.amount, selectedPayment.currency)}</p>
                </div>
              </div>

              {/* Bank Info */}
              {(selectedPayment.bank_account_holder_name || selectedPayment.payee_bank_account_name || selectedPayment.bank_reference_number) && (
                <div className="p-3 bg-emerald-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-sm text-emerald-400 font-medium mb-1">Bank Details</p>
                  {selectedPayment.bank_account_holder_name && <p className="text-sm text-white">Holder: <strong>{selectedPayment.bank_account_holder_name}</strong></p>}
                  {selectedPayment.payee_bank_account_name && <p className="text-sm text-white">Payee: <strong>{selectedPayment.payee_bank_account_name}</strong></p>}
                  {selectedPayment.bank_reference_number && <p className="text-sm text-cyan-400 font-mono">Ref: {selectedPayment.bank_reference_number}</p>}
                </div>
              )}

              {/* Status & Method */}
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-slate-400">Method</p><div className="mt-1"><MethodBadge method={selectedPayment.payment_method} /></div></div>
                <div><p className="text-sm text-slate-400">Status</p><div className="mt-1"><StatusBadge status={selectedPayment.payment_status} /></div></div>
              </div>

              {/* Dispute info if any */}
              {selectedPayment.dispute_reason && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-sm text-amber-400 font-medium">Dispute Reason</p>
                  <p className="text-sm text-white mt-1">{selectedPayment.dispute_reason}</p>
                  {(selectedPayment.amount_received ?? selectedPayment.received_amount) != null && (
                    <p className="text-xs text-slate-400 mt-1">Received: ${(selectedPayment.amount_received ?? selectedPayment.received_amount)?.toFixed(2)} | Expected: ${(selectedPayment.amount ?? selectedPayment.expected_amount)?.toFixed(2)}</p>
                  )}
                </div>
              )}

              {/* Fee Breakdown */}
              {selectedPayment.fee_breakdown && (
                <div className="border-t border-slate-700 pt-3">
                  <p className="text-sm text-slate-400 mb-2 font-medium">Fee Breakdown</p>
                  <div className="bg-[#0a0f1a] rounded-lg p-3 space-y-1.5 text-sm">
                    {selectedPayment.fee_breakdown.base_fee > 0 && <div className="flex justify-between"><span>Session Fees</span><span>${parseFloat(selectedPayment.fee_breakdown.base_fee).toFixed(2)}</span></div>}
                    {selectedPayment.fee_breakdown.registration_fee > 0 && <div className="flex justify-between"><span>Registration Fee</span><span>${parseFloat(selectedPayment.fee_breakdown.registration_fee).toFixed(2)}</span></div>}
                    {selectedPayment.fee_breakdown.sibling_discount > 0 && <div className="flex justify-between text-emerald-400"><span>Sibling Discount</span><span>-${parseFloat(selectedPayment.fee_breakdown.sibling_discount).toFixed(2)}</span></div>}
                    {selectedPayment.fee_breakdown.dm_price > 0 && <div className="flex justify-between"><span>Digital Membership</span><span>${parseFloat(selectedPayment.fee_breakdown.dm_price).toFixed(2)}</span></div>}
                    <div className="flex justify-between font-bold pt-2 border-t border-slate-700"><span>TOTAL</span><span className="text-emerald-400">${parseFloat(selectedPayment.amount || 0).toFixed(2)}</span></div>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setSelectedPayment(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== APPROVE DIALOG (Enhanced) ===== */}
      <Dialog open={approveDialog.open} onOpenChange={() => setApproveDialog({ open: false, payment: null })}>
        <DialogContent className="max-w-md" data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle className="text-emerald-400">Approve Payment</DialogTitle>
            <DialogDescription>Verify details and add bank reference information.</DialogDescription>
          </DialogHeader>
          {approveDialog.payment && (
            <div className="space-y-4">
              <div className="bg-[#0a0f1a] rounded-lg p-3 space-y-2">
                <p className="font-mono font-bold text-blue-400">{approveDialog.payment.payment_reference}</p>
                <p className="font-medium text-sm">{approveDialog.payment.parent_name}</p>
                <ChildrenInfo payment={approveDialog.payment} />
                <div className="flex justify-between items-center pt-2 border-t border-slate-700">
                  <span className="text-sm text-slate-400">Amount</span>
                  <span className="text-lg font-bold text-emerald-400">{formatCurrency(approveDialog.payment.amount, approveDialog.payment.currency)}</span>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="text-slate-300 text-sm">Payment Reference Number (from bank)</Label>
                  <Input value={approveData.bankRef} onChange={(e) => setApproveData(p => ({ ...p, bankRef: e.target.value }))}
                    placeholder="e.g., TRANSFER-2034536588" className="mt-1 bg-slate-800/50 border-slate-700 text-white" data-testid="approve-bank-ref" />
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Payee Bank Account Name</Label>
                  <Input value={approveData.payeeName} onChange={(e) => setApproveData(p => ({ ...p, payeeName: e.target.value }))}
                    placeholder="Name on the bank account" className="mt-1 bg-slate-800/50 border-slate-700 text-white" data-testid="approve-payee-name" />
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Notes (optional)</Label>
                  <Input value={approveData.notes} onChange={(e) => setApproveData(p => ({ ...p, notes: e.target.value }))}
                    placeholder="Any additional notes" className="mt-1 bg-slate-800/50 border-slate-700 text-white" data-testid="approve-notes" />
                </div>
                <div className="flex items-center space-x-2 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                  <Checkbox id="approve-children" checked={approveData.approveChildren}
                    onCheckedChange={(checked) => setApproveData(p => ({ ...p, approveChildren: checked }))} />
                  <Label htmlFor="approve-children" className="text-emerald-400 text-sm cursor-pointer">Also approve child(ren) for academy</Label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialog({ open: false, payment: null })}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={handleEnhancedApprove}
              disabled={actionLoading === approveDialog.payment?.id} data-testid="confirm-approve-btn">
              {actionLoading === approveDialog.payment?.id ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Processing...</> : <><CheckCircle className="w-4 h-4 mr-2" />Approve Payment</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== REJECT DIALOG ===== */}
      <Dialog open={rejectDialog.open} onOpenChange={() => setRejectDialog({ open: false, payment: null })}>
        <DialogContent className="max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-400">Reject Payment</DialogTitle>
            <DialogDescription>Provide a reason. Parent will be notified.</DialogDescription>
          </DialogHeader>
          {rejectDialog.payment && (
            <div className="space-y-4">
              <div className="bg-[#0a0f1a] rounded-lg p-3">
                <p className="font-mono text-blue-400">{rejectDialog.payment.payment_reference}</p>
                <p className="text-sm mt-1">{rejectDialog.payment.parent_name} - {formatCurrency(rejectDialog.payment.amount)}</p>
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Reason for Rejection</Label>
                <textarea className="w-full mt-1 p-3 bg-slate-900 border border-red-500/30 rounded-lg text-white placeholder:text-slate-400 text-sm"
                  rows={3} placeholder="e.g., Payment not received, incorrect amount..." value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)} data-testid="reject-reason-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectDialog({ open: false, payment: null }); setRejectionReason(""); }}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleReject}
              disabled={actionLoading === rejectDialog.payment?.id} data-testid="confirm-reject-btn">
              {actionLoading === rejectDialog.payment?.id ? "Processing..." : <><XCircle className="w-4 h-4 mr-2" />Reject Payment</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== DISPUTE DIALOG ===== */}
      <Dialog open={disputeDialog.open} onOpenChange={() => setDisputeDialog({ open: false, payment: null })}>
        <DialogContent className="max-w-md" data-testid="dispute-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-400">Payment Verification Query</DialogTitle>
            <DialogDescription>Send a polite enquiry to the parent about their payment details.</DialogDescription>
          </DialogHeader>
          {disputeDialog.payment && (
            <div className="space-y-4">
              <div className="bg-[#0a0f1a] p-3 rounded-lg">
                <p className="font-mono text-blue-400">{disputeDialog.payment.payment_reference}</p>
                <p className="text-sm mt-1">{disputeDialog.payment.parent_name}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-400">Expected Amount</Label>
                  <div className="p-3 bg-blue-500/10 rounded border border-blue-500/30 text-center">
                    <p className="text-xl font-bold text-blue-400">${disputeDialog.payment.amount?.toFixed(2)}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Amount Received</Label>
                  <Input type="number" step="0.01" placeholder="e.g., 423.00" value={disputeData.receivedAmount}
                    onChange={(e) => setDisputeData(p => ({ ...p, receivedAmount: e.target.value }))}
                    className="text-center text-lg" data-testid="dispute-received-amount" />
                </div>
              </div>
              {disputeData.receivedAmount && (
                <div className="p-2 bg-amber-500/10 rounded text-center">
                  <p className="text-xs text-slate-400">Difference</p>
                  <p className="text-lg font-bold text-amber-400">${(disputeDialog.payment.amount - parseFloat(disputeData.receivedAmount || 0)).toFixed(2)}</p>
                </div>
              )}
              <div>
                <Label className="text-sm text-slate-300">Details / Message to Parent</Label>
                <textarea className="w-full mt-1 p-3 bg-slate-900 border border-amber-500/30 rounded-lg text-white placeholder:text-slate-400 text-sm"
                  rows={3} placeholder="e.g., We received $423 but expected $470. Could you confirm the amount transferred?"
                  value={disputeData.reason} onChange={(e) => setDisputeData(p => ({ ...p, reason: e.target.value }))} data-testid="dispute-reason-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDisputeDialog({ open: false, payment: null }); setDisputeData({ reason: "", receivedAmount: "" }); }}>Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleDispute}
              disabled={actionLoading === disputeDialog.payment?.id || !disputeData.reason} data-testid="confirm-dispute-btn">
              {actionLoading === disputeDialog.payment?.id ? "Processing..." : "Send Verification Query"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== REVERT DIALOG ===== */}
      <Dialog open={revertDialog.open} onOpenChange={() => setRevertDialog({ open: false, payment: null })}>
        <DialogContent className="max-w-md" data-testid="revert-dialog">
          <DialogHeader>
            <DialogTitle className="text-orange-400">Revert Payment to Pending</DialogTitle>
            <DialogDescription>This will move the payment back to pending verification for review.</DialogDescription>
          </DialogHeader>
          {revertDialog.payment && (
            <div className="space-y-4">
              <div className="bg-[#0a0f1a] rounded-lg p-3">
                <p className="font-mono text-blue-400">{revertDialog.payment.payment_reference}</p>
                <p className="text-sm mt-1">{revertDialog.payment.parent_name} - {formatCurrency(revertDialog.payment.amount)}</p>
                <StatusBadge status={revertDialog.payment.payment_status} />
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Reason for Reverting</Label>
                <textarea className="w-full mt-1 p-3 bg-slate-900 border border-orange-500/30 rounded-lg text-white placeholder:text-slate-400 text-sm"
                  rows={3} placeholder="e.g., Need to re-verify payment details..." value={revertReason}
                  onChange={(e) => setRevertReason(e.target.value)} data-testid="revert-reason-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRevertDialog({ open: false, payment: null }); setRevertReason(""); }}>Cancel</Button>
            <Button className="bg-orange-600 hover:bg-orange-700 text-white" onClick={handleRevert}
              disabled={actionLoading === revertDialog.payment?.id} data-testid="confirm-revert-btn">
              {actionLoading === revertDialog.payment?.id ? "Processing..." : <><RotateCcw className="w-4 h-4 mr-2" />Revert to Pending</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Feb 2026 — REFUND DIALOG ===== */}
      <Dialog open={refundDialog.open} onOpenChange={() => setRefundDialog({ open: false, payment: null })}>
        <DialogContent className="max-w-md" data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-400">Mark Payment as Refunded</DialogTitle>
            <DialogDescription>
              The transaction will be flagged <strong>refunded</strong> and removed from active queues.
              The linked registration will also flip to refunded so the parent no longer
              shows as enrolled. The record itself is preserved for audit.
            </DialogDescription>
          </DialogHeader>
          {refundDialog.payment && (
            <div className="space-y-4">
              <div className="bg-[#0a0f1a] rounded-lg p-3">
                <p className="font-mono text-blue-400">{refundDialog.payment.payment_reference}</p>
                <p className="text-sm mt-1">{refundDialog.payment.parent_name} - {formatCurrency(refundDialog.payment.amount)}</p>
                <StatusBadge status={refundDialog.payment.payment_status} />
              </div>
              <div>
                <Label className="text-slate-300 text-sm">
                  Refund Reason <span className="text-rose-400">*</span>
                </Label>
                <textarea
                  className="w-full mt-1 p-3 bg-slate-900 border border-rose-500/30 rounded-lg text-white placeholder:text-slate-400 text-sm"
                  rows={3}
                  placeholder="e.g., wrong amount, double charge, parent requested cancellation..."
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  data-testid="refund-reason-input"
                />
              </div>
              <div className="text-[11px] text-slate-400 bg-slate-900/40 rounded p-2 border border-slate-700">
                <p className="font-medium mb-0.5">What happens:</p>
                <ul className="list-disc ml-4 space-y-0.5">
                  <li>Transaction status → <code className="text-rose-300">refunded</code></li>
                  <li>Linked registration status → <code className="text-rose-300">refunded</code></li>
                  <li>
                    Audit note appended:
                    <em> Refunded by admin — &lt;reason&gt; — &lt;date&gt;</em>
                  </li>
                  <li>Parent receives a refund-confirmation email</li>
                  <li>Record is preserved (no deletion)</li>
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setRefundDialog({ open: false, payment: null }); setRefundReason(""); }}
              data-testid="cancel-refund-btn"
            >
              Cancel
            </Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={handleRefund}
              disabled={
                actionLoading === refundDialog.payment?.id ||
                !refundReason.trim()
              }
              data-testid="confirm-refund-btn"
            >
              {actionLoading === refundDialog.payment?.id
                ? "Processing..."
                : <><XCircle className="w-4 h-4 mr-2" />Mark as Refunded</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== DELETE DIALOG ===== */}
      <Dialog open={deleteDialog.open} onOpenChange={() => setDeleteDialog({ open: false, payment: null })}>
        <DialogContent className="max-w-sm" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-400">Delete Payment Record</DialogTitle>
            <DialogDescription>This permanently removes this payment from the system.</DialogDescription>
          </DialogHeader>
          {deleteDialog.payment && (
            <div className="bg-[#0a0f1a] rounded-lg p-3">
              <p className="font-mono text-blue-400">{deleteDialog.payment.payment_reference}</p>
              <p className="text-sm mt-1">{deleteDialog.payment.parent_name} - {formatCurrency(deleteDialog.payment.amount)}</p>
              <StatusBadge status={deleteDialog.payment.payment_status} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, payment: null })}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDelete}
              disabled={actionLoading === deleteDialog.payment?.id} data-testid="confirm-delete-btn">
              {actionLoading === deleteDialog.payment?.id ? "Deleting..." : <><Trash2 className="w-4 h-4 mr-2" />Delete</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== BANK MANUAL LINK DIALOG ===== */}
      <Dialog open={linkDialog.open} onOpenChange={() => setLinkDialog({ open: false, entry: null })}>
        <DialogContent className="max-w-md" data-testid="link-dialog">
          <DialogHeader>
            <DialogTitle>Link Bank Entry to Payment</DialogTitle>
            <DialogDescription>Select or enter the payment transaction to link this bank entry to.</DialogDescription>
          </DialogHeader>
          {linkDialog.entry && (
            <div className="space-y-4">
              <div className="bg-[#0a0f1a] rounded-lg p-3">
                <p className="text-sm text-slate-400">Bank Entry</p>
                <p className="font-medium text-white">{linkDialog.entry.source_name}</p>
                <p className="text-emerald-400 font-semibold">${(linkDialog.entry.source_amount || linkDialog.entry.target_amount || 0).toFixed(2)}</p>
                <p className="text-xs text-slate-500">{linkDialog.entry.created_on}</p>
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Select Payment Transaction</Label>
                <Select value={linkPaymentId} onValueChange={setLinkPaymentId}>
                  <SelectTrigger className="mt-1 bg-slate-800/50 border-slate-700 text-white" data-testid="link-payment-select">
                    <SelectValue placeholder="Choose a payment..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allPayments
                      .filter(p => ["pending_verification", "disputed", "paid", "complete"].includes(p.payment_status))
                      .map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.payment_reference} - {p.parent_name} - ${p.amount?.toFixed(2)}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialog({ open: false, entry: null })}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleManualLink}
              disabled={!linkPaymentId || actionLoading === linkDialog.entry?.id} data-testid="confirm-link-btn">
              <Link2 className="w-4 h-4 mr-2" />Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== FEE CALCULATOR MODAL ===== */}
      <Dialog open={showFeeCalculator} onOpenChange={setShowFeeCalculator}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Calculator className="w-5 h-5 text-purple-400" />Fee Calculator
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Parent Name</Label>
                <Input value={feeParams.parent_name} onChange={(e) => setFeeParams(prev => ({ ...prev, parent_name: e.target.value }))} placeholder="Parent" className="bg-slate-800/50 border-slate-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Child Name</Label>
                <Input value={feeParams.child_name} onChange={(e) => setFeeParams(prev => ({ ...prev, child_name: e.target.value }))} placeholder="Child" className="bg-slate-800/50 border-slate-700 text-white" />
              </div>
            </div>
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2"><Calendar className="w-4 h-4" /> Term</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Select Term</Label>
                  <Select value={feeParams.term_id} onValueChange={(value) => { const t = terms.find(t => t.id === value); setFeeParams(prev => ({ ...prev, term_id: value, term_weeks: t?.total_weeks || prev.term_weeks })); }}>
                    <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Custom</SelectItem>
                      {terms.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Weeks</Label>
                  <Input type="number" min={1} max={52} value={feeParams.term_weeks} onChange={(e) => setFeeParams(prev => ({ ...prev, term_weeks: parseInt(e.target.value) || 10 }))} className="bg-slate-700/50 border-slate-600 text-white" />
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> Sessions & Children</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Sessions/Week</Label>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="icon" className="h-8 w-8 border-slate-600" onClick={() => setFeeParams(prev => ({ ...prev, sessions_per_week: Math.max(1, prev.sessions_per_week - 1) }))}><Minus className="w-3 h-3" /></Button>
                    <span className="text-white font-bold text-lg w-8 text-center">{feeParams.sessions_per_week}</span>
                    <Button type="button" variant="outline" size="icon" className="h-8 w-8 border-slate-600" onClick={() => setFeeParams(prev => ({ ...prev, sessions_per_week: Math.min(7, prev.sessions_per_week + 1) }))}><Plus className="w-3 h-3" /></Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Children</Label>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="icon" className="h-8 w-8 border-slate-600" onClick={() => setFeeParams(prev => ({ ...prev, num_children: Math.max(1, prev.num_children - 1), child_number: Math.min(prev.child_number, Math.max(1, prev.num_children - 1)) }))}><Minus className="w-3 h-3" /></Button>
                    <span className="text-white font-bold text-lg w-8 text-center">{feeParams.num_children}</span>
                    <Button type="button" variant="outline" size="icon" className="h-8 w-8 border-slate-600" onClick={() => setFeeParams(prev => ({ ...prev, num_children: Math.min(5, prev.num_children + 1) }))}><Plus className="w-3 h-3" /></Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Child #</Label>
                  <Select value={feeParams.child_number.toString()} onValueChange={(val) => setFeeParams(prev => ({ ...prev, child_number: parseInt(val) }))}>
                    <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: feeParams.num_children }, (_, i) => i + 1).map(num => (
                        <SelectItem key={num} value={num.toString()}>{num === 1 ? "1st" : `${num}${num === 2 ? 'nd' : num === 3 ? 'rd' : 'th'}`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <h4 className="text-sm font-medium text-slate-300 mb-3">Membership</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox id="calc_scc" checked={feeParams.is_scc_member} onCheckedChange={(c) => setFeeParams(p => ({ ...p, is_scc_member: c }))} />
                  <Label htmlFor="calc_scc" className="text-slate-300 text-sm cursor-pointer">SCC Member</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="calc_section" checked={feeParams.is_section_member} onCheckedChange={(c) => setFeeParams(p => ({ ...p, is_section_member: c }))} />
                  <Label htmlFor="calc_section" className="text-slate-300 text-sm cursor-pointer">Section</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="calc_dm" checked={feeParams.is_digital_member} onCheckedChange={(c) => setFeeParams(p => ({ ...p, is_digital_member: c }))} />
                  <Label htmlFor="calc_dm" className="text-slate-300 text-sm cursor-pointer">Digital</Label>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <h4 className="text-sm font-medium text-slate-300 mb-3">Extras</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox id="calc_reg" checked={feeParams.include_registration_fee} onCheckedChange={(c) => setFeeParams(p => ({ ...p, include_registration_fee: c }))} />
                  <Label htmlFor="calc_reg" className="text-slate-300 text-sm cursor-pointer">Registration Fee</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="calc_adhoc" checked={feeParams.include_ad_hoc} onCheckedChange={(c) => setFeeParams(p => ({ ...p, include_ad_hoc: c, ad_hoc_sessions: c ? 1 : 0 }))} />
                  <Label htmlFor="calc_adhoc" className="text-slate-300 text-sm cursor-pointer">Ad-hoc</Label>
                  {feeParams.include_ad_hoc && <Input type="number" min={1} max={20} value={feeParams.ad_hoc_sessions} onChange={(e) => setFeeParams(p => ({ ...p, ad_hoc_sessions: parseInt(e.target.value) || 0 }))} className="w-16 h-8 bg-slate-700/50 border-slate-600 text-white ml-2" />}
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <h4 className="text-sm font-medium text-slate-300 mb-3"><TrendingDown className="w-4 h-4 inline mr-1" />Compare</h4>
              <Input type="number" step="0.01" value={feeParams.actual_paid} onChange={(e) => setFeeParams(p => ({ ...p, actual_paid: e.target.value }))} placeholder="Amount paid (optional)" className="bg-slate-700/50 border-slate-600 text-white" />
            </Card>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowFeeCalculator(false)}>Cancel</Button>
            <Button onClick={calculateFee} disabled={calculatingFee} className="bg-gradient-to-r from-purple-500 to-indigo-600">
              {calculatingFee ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Calculating...</> : <><Calculator className="w-4 h-4 mr-2" />Calculate</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== FEE RESULT MODAL ===== */}
      <Dialog open={showFeeResult} onOpenChange={setShowFeeResult}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-400" />Fee Calculation Result</DialogTitle>
          </DialogHeader>
          {feeCalcResult && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <Card className="p-4 bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30">
                  <p className="text-xs text-slate-400 mb-1">Calculated</p>
                  <p className="text-3xl font-bold text-emerald-400">${feeCalcResult.calculation?.calculated_total?.toFixed(2)}</p>
                </Card>
                {feeCalcResult.comparison?.actual_paid !== null && (
                  <>
                    <Card className="p-4 bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30">
                      <p className="text-xs text-slate-400 mb-1">Paid</p>
                      <p className="text-3xl font-bold text-blue-400">${feeCalcResult.comparison?.actual_paid?.toFixed(2)}</p>
                    </Card>
                    <Card className={`p-4 bg-gradient-to-br border ${feeCalcResult.comparison?.status === "exact_match" ? "border-emerald-500/30" : feeCalcResult.comparison?.status === "overpaid" ? "border-amber-500/30" : "border-red-500/30"}`}>
                      <p className="text-xs text-slate-400 mb-1">Diff</p>
                      <p className={`text-3xl font-bold ${feeCalcResult.comparison?.status === "exact_match" ? "text-emerald-400" : feeCalcResult.comparison?.status === "overpaid" ? "text-amber-400" : "text-red-400"}`}>
                        {feeCalcResult.comparison?.status === "exact_match" ? <span className="flex items-center gap-2"><CheckCircle className="w-8 h-8" />Match!</span> : <>{feeCalcResult.comparison?.difference > 0 ? "+" : ""}${feeCalcResult.comparison?.difference?.toFixed(2)}</>}
                      </p>
                    </Card>
                  </>
                )}
              </div>
              {feeCalcResult.calculation?.breakdown?.session_breakdown && (
                <Card className="p-4 bg-slate-800/50 border-slate-700">
                  <h4 className="text-sm font-medium text-slate-300 mb-3">Breakdown</h4>
                  <div className="space-y-2">
                    {feeCalcResult.calculation.breakdown.session_breakdown.map((s, idx) => (
                      <div key={idx} className="flex justify-between py-2 px-3 bg-slate-700/50 rounded text-sm">
                        <span>Session {s.session_number}</span>
                        <span>${s.final_price?.toFixed(2)}{s.membership_discount > 0 && <span className="text-xs text-emerald-400 ml-2">(-${s.membership_discount?.toFixed(2)})</span>}</span>
                      </div>
                    ))}
                    {feeCalcResult.calculation?.breakdown?.sibling_discount > 0 && (
                      <div className="flex justify-between py-2 px-3 bg-emerald-500/10 rounded text-sm text-emerald-400">
                        <span>Sibling Discount</span><span>-${feeCalcResult.calculation.breakdown.sibling_discount?.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between py-3 px-3 bg-purple-500/20 rounded text-base font-bold border border-purple-500/30">
                      <span>Total</span><span className="text-purple-400">${feeCalcResult.calculation?.calculated_total?.toFixed(2)}</span>
                    </div>
                  </div>
                </Card>
              )}
              {(feeCalcResult.comparison?.status === "underpaid" || feeCalcResult.comparison?.status === "not_compared") && feeCalcResult.qr_data && (
                <Card className="p-4 bg-slate-800/50 border-slate-700" ref={feeQrRef}>
                  <h4 className="text-sm font-medium text-slate-300 mb-4 flex items-center gap-2"><QrCode className="w-4 h-4" />Payment QR</h4>
                  <div className="flex gap-6 items-start">
                    <div className="bg-white p-4 rounded-lg">
                      <QRCodeSVG value={generatePayNowQR(
                        feeCalcResult.comparison?.status === "underpaid" 
                          ? Math.abs(feeCalcResult.comparison?.difference || 0) 
                          : feeCalcResult.qr_data?.amount || 0
                      )} size={150} level="M" includeMargin={true} />
                    </div>
                    <div className="flex-1 space-y-3">
                      <div>
                        <p className="text-xs text-slate-500">Pay To</p>
                        <p className="text-white font-medium">{feeCalcResult.qr_data?.company}</p>
                        <p className="text-slate-400 text-sm">UEN: {feeCalcResult.qr_data?.uen}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Amount</p>
                        <p className="text-2xl font-bold text-emerald-400">${feeCalcResult.comparison?.status === "underpaid" ? Math.abs(feeCalcResult.comparison?.difference || 0).toFixed(2) : feeCalcResult.qr_data?.amount?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Reference</p>
                        <div className="flex items-center gap-2">
                          <code className="text-yellow-400 bg-slate-700/50 px-2 py-1 rounded">{feeCalcResult.qr_data?.reference}</code>
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={copyPaymentRef}>{copiedRef ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}</Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          )}
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowFeeResult(false)}>Close</Button>
            {feeCalcResult?.qr_data && <Button onClick={downloadFeeQR} className="bg-gradient-to-r from-emerald-500 to-teal-600"><Download className="w-4 h-4 mr-2" />Download QR</Button>}
            <Button onClick={() => { setShowFeeResult(false); setShowFeeCalculator(true); }} className="bg-gradient-to-r from-purple-500 to-indigo-600"><Calculator className="w-4 h-4 mr-2" />Recalculate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== DOCUMENT PREVIEW DIALOG ===== */}
      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="document-preview-dialog">
          <DialogHeader>
            <DialogTitle>{previewDoc?.doc_type === "invoice" ? "Invoice" : "Receipt"} Preview</DialogTitle>
            <DialogDescription>
              {previewDoc?.payment_id} | {previewDoc?.parent_name} | ${(previewDoc?.amount || 0).toFixed(2)}
            </DialogDescription>
          </DialogHeader>
          {previewDoc?.html_content && (
            <div className="bg-white rounded-lg overflow-hidden" style={{ minHeight: "300px" }}>
              <iframe
                srcDoc={previewDoc.html_content}
                title="Document Preview"
                className="w-full border-0"
                style={{ minHeight: "500px" }}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDoc(null)}>Close</Button>
            <Button className="bg-blue-600 hover:bg-blue-700"
              onClick={() => handleDownloadDocument(previewDoc?.payment_id, previewDoc?.doc_type)}>
              <Download className="w-4 h-4 mr-2" />Open in New Tab
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}